/**
 * MemberActionTimeline — timeline of past actions taken on a member, with
 * computed outcomes (engaged / no_response / pending). Real data comes via
 * the get_member_action_timeline RPC.
 */

import React from 'react';
import { Loader2, Mail, PenLine, LayoutGrid, UserPlus, Flag, Pause } from 'lucide-react';
import { useMemberActionTimeline } from '@/hooks/useMemberActionTimeline';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { formatDistanceToNow } from 'date-fns';

interface Props {
  memberId: string;
  clubId: string;
}

export const MemberActionTimeline: React.FC<Props> = ({ memberId, clubId }) => {
  const { t } = useAppTranslations();
  const { data, isLoading, error } = useMemberActionTimeline(memberId, clubId);

  const ACTION_TYPE_META: Record<string, { label: string; icon: React.ElementType; color: string }> = {
    re_engagement:  { label: t.crm.timeline.actionTypes.reEngagement,   icon: Mail,       color: '#2A4494' },
    log_followup:   { label: t.crm.timeline.actionTypes.followUp,       icon: PenLine,    color: '#0891B2' },
    comp_something: { label: t.crm.timeline.actionTypes.comp,           icon: LayoutGrid, color: '#7C3AED' },
    reassign_coach: { label: t.crm.timeline.actionTypes.reassignCoach,  icon: UserPlus,   color: '#059669' },
    snooze:         { label: t.crm.timeline.actionTypes.markedHandled,  icon: Pause,      color: '#D97706' },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-slate-300 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <p className="text-[11px] text-red-500">{t.crm.timeline.couldNotLoad}</p>;
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-[12px] text-slate-400">{t.crm.timeline.noActionsTitle}</p>
        <p className="text-[11px] text-slate-300 mt-1">{t.crm.timeline.noActionsHint}</p>
      </div>
    );
  }

  return (
    <div className="relative pl-6">
      <div className="absolute left-[7px] top-2 bottom-0 w-0.5 bg-slate-100" />

      {data.map(entry => {
        const meta = ACTION_TYPE_META[entry.actionType] ?? { label: entry.actionType, icon: Flag, color: '#94A3B8' };
        const Icon = meta.icon;
        const time = formatDistanceToNow(new Date(entry.performedAt), { addSuffix: true });

        // Outcome row colour
        const outcomeColor =
          entry.outcome === 'engaged' ? '#16a34a'
          : entry.outcome === 'no_response' ? '#dc2626'
          : '#94a3b8';
        const outcomeText =
          entry.outcome === 'engaged' ? t.crm.timeline.outcomeEngaged
          : entry.outcome === 'no_response' ? t.crm.timeline.outcomeNoResponse
          : entry.actionType === 'snooze' ? t.crm.timeline.outcomeAcknowledged
          : t.crm.timeline.outcomePending;

        const cardBg =
          entry.outcome === 'engaged' ? 'bg-green-50 border-green-200'
          : entry.outcome === 'no_response' ? 'bg-red-50 border-red-200'
          : 'bg-slate-50 border-slate-200';

        return (
          <div key={entry.id} className="relative mb-4">
            <div
              className="absolute -left-6 top-1 w-4 h-4 rounded-full flex items-center justify-center text-white"
              style={{ background: meta.color }}
            >
              <Icon className="w-2.5 h-2.5" />
            </div>
            <div className={`border rounded-xl px-3 py-2.5 ${cardBg}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>
                  {meta.label}
                </span>
                <span className="text-[10px] text-slate-400">{time}</span>
              </div>
              <div className="text-[11px] text-slate-600 leading-snug">
                {describePayload(entry.actionType, entry.payload, entry.performedByName)}
              </div>
              <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-200/60">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: outcomeColor }} />
                <span className="text-[10px] font-semibold" style={{ color: outcomeColor }}>
                  {outcomeText}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

function describePayload(actionType: string, payload: Record<string, unknown> | null, byName: string | null): string {
  const by = byName ? ` · by ${byName}` : '';
  if (!payload) return `Action${by}`;

  if (actionType === 're_engagement' && typeof payload.template === 'string') {
    return `Template: "${payload.template}"${by}`;
  }
  if (actionType === 'log_followup' && typeof payload.note === 'string') {
    return `"${payload.note}"${by}`;
  }
  if (actionType === 'comp_something' && typeof payload.comp_type === 'string') {
    return `Offered: ${payload.comp_type}${by}`;
  }
  if (actionType === 'snooze') {
    if (payload.undo === true) return `Snooze removed${by}`;
    if (typeof payload.duration_days === 'number') return `Snoozed for ${payload.duration_days}d${by}`;
    return `Marked handled${by}`;
  }
  return `Action${by}`;
}
